import { faRoute } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import Helper from "../../../Helper";
import { HaulRoute } from "../../../models/HaulRoute";

// Mirrors Google Maps' route-picker: once a route is picked, the alternates are drawn grey.
export const ALTERNATE_HAUL_ROUTE_COLOR = "#9aa0a6";

interface HaulRouteSelectorProps {
    haulRoutes: HaulRoute[];
    selectedHaulRouteId: number | null;
    disabled?: boolean;
    onSelect: (haulRoute: HaulRoute) => void;
    // Lets the caller hang content (e.g. turn-by-turn steps) directly off the selected route instead of below the list.
    renderSelected?: (haulRoute: HaulRoute, button: React.ReactNode) => React.ReactNode;
}

const HaulRouteSelector: React.FC<HaulRouteSelectorProps> = ({
    haulRoutes,
    selectedHaulRouteId,
    disabled = false,
    onSelect,
    renderSelected,
}) => {
    const context = React.useContext(GlobalStoreContext);

    if (haulRoutes.length === 0) {
        return null;
    }

    const hasSelection = haulRoutes.some((haulRoute) => haulRoute.id === selectedHaulRouteId);

    return (
        <div className={Helper.classNames("flex flex-col gap-2 pb-1", !renderSelected && "lg:flex-row")}>
            {haulRoutes.map((haulRoute) => {
                const mapObject = haulRoute.getMapObject(context);
                const lineColor = mapObject?.outline || mapObject?.color || ALTERNATE_HAUL_ROUTE_COLOR;
                const isSelected = haulRoute.id === selectedHaulRouteId;
                const swatchColor = hasSelection && !isSelected ? ALTERNATE_HAUL_ROUTE_COLOR : lineColor;

                const button = (
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onSelect(haulRoute)}
                        className={Helper.classNames(
                            "flex items-center w-full gap-2 shrink-0 rounded-xl text-left border px-3 py-2 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60",
                            isSelected
                                ? "border-plot-blue bg-plot-blue/10 text-plot-blue"
                                : "border-plot-gray bg-plot-gray/10 text-plot-gray hover:bg-gray-100"
                        )}
                    >
                        <span
                            className="inline-block h-1.5 w-6 rounded-full shrink-0"
                            style={{
                                backgroundColor: swatchColor,
                            }}
                        />
                        <FontAwesomeIcon icon={faRoute} className={isSelected ? "text-[#1a73e8]" : "text-gray-400"} />
                        {haulRoute.name}
                    </button>
                );

                return (
                    <React.Fragment key={haulRoute.id}>
                        {isSelected && renderSelected ? renderSelected(haulRoute, button) : button}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default HaulRouteSelector;
